/// <amd-module name="Repos/Changes/Components/Diff/InlineDiff" />
import * as React from "react";
import * as Resources from "Repos/Changes/Resources";
import { FileDiffBlockChangeType, IFileDiff } from "Repos/ChangesCommon/Contracts";
import { CommentThreadContext } from "Repos/Common/Contracts/Git";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { EllipsisLine, IDiffSummaryDiscussionWidgetProvider, maxLinesRendered, processCharDiffChanges, processLineDiffChanges } from "./DiffBlock";

export interface IInlineDiffProps {
    commentPositions?: CommentThreadContext[];
    fileDiff: IFileDiff;
    hideOriginalLineNumbers?: boolean;
    widgetProvider?: IDiffSummaryDiscussionWidgetProvider;
}

export const InlineDiff = (props: IInlineDiffProps): JSX.Element => {
    const { commentPositions, fileDiff, hideOriginalLineNumbers, widgetProvider } = props;
    const diffBlocks: JSX.Element[] = [];
    let contentTruncated = false;
    let editLinesRendered = 0;
    let ellipsisLinesRendered = 0;
    let linesRendered = 0;

    let oLinesExist = !!fileDiff.originalFile;
    const mLinesExist = !!fileDiff.modifiedFile;
    if (oLinesExist && mLinesExist && hideOriginalLineNumbers) {
        oLinesExist = false;
    }

    if (fileDiff && !fileDiff.imageComparison && !fileDiff.binaryContent && !fileDiff.whitespaceChangesOnly) {
        for (let i = 0; i < fileDiff.lineCharBlocks.length; i++) {
            let currentLinesRendered = linesRendered;
            // Total lines - (ellipsis lines + editLines), gets us to not double-count edit lines
            const currentDisplayLine = currentLinesRendered - ellipsisLinesRendered - editLinesRendered;
            const diffSection = fileDiff.lineCharBlocks[i];
            const fileName = fileDiff.modifiedFile?.contentMetadata.fileName || fileDiff.originalFile?.contentMetadata.fileName || "";

            if (i === 0 && diffSection.lineChange.truncatedBefore) {
                diffBlocks.push(
                    <EllipsisLine key={`ellipsis-lines-${i}`} mLinesExist={mLinesExist} oLinesExist={oLinesExist} widgetProvider={widgetProvider} />
                );
                linesRendered++;
                ellipsisLinesRendered++;
            }

            if (
                diffSection.lineChange.changeType === FileDiffBlockChangeType.None ||
                diffSection.lineChange.changeType === FileDiffBlockChangeType.Edit ||
                diffSection.lineChange.changeType === FileDiffBlockChangeType.Add
            ) {
                linesRendered += diffSection.lineChange.mLines.length;
            }

            if (
                diffSection.lineChange.changeType === FileDiffBlockChangeType.Edit ||
                diffSection.lineChange.changeType === FileDiffBlockChangeType.Delete
            ) {
                linesRendered += diffSection.lineChange.oLines.length;
            }

            contentTruncated = linesRendered > maxLinesRendered;
            if (currentLinesRendered < maxLinesRendered) {
                const result: JSX.Element[] = [];

                const lineChanges = diffSection.lineChange;

                // Loop through all changes for this diff block.  This can be an Add/Delete/Edit/None.
                // Depending on the type, add to appropriate diff and possibly inline character highlighting for edits.
                if (diffSection.lineChange.changeType === FileDiffBlockChangeType.None) {
                    result.push(
                        ...processLineDiffChanges(
                            fileName,
                            currentLinesRendered,
                            currentDisplayLine,
                            lineChanges.mLines,
                            lineChanges.oLine,
                            lineChanges.mLine,
                            "unchanged",
                            oLinesExist,
                            mLinesExist,
                            widgetProvider,
                            commentPositions
                        )
                    );
                } else if (diffSection.lineChange.changeType === FileDiffBlockChangeType.Add) {
                    result.push(
                        ...processLineDiffChanges(
                            fileName,
                            currentLinesRendered,
                            currentDisplayLine,
                            lineChanges.mLines,
                            undefined,
                            lineChanges.mLine,
                            "added",
                            oLinesExist,
                            mLinesExist,
                            widgetProvider,
                            commentPositions
                        )
                    );
                } else if (diffSection.lineChange.changeType === FileDiffBlockChangeType.Delete) {
                    result.push(
                        ...processLineDiffChanges(
                            fileName,
                            currentLinesRendered,
                            currentDisplayLine,
                            lineChanges.oLines,
                            lineChanges.oLine,
                            undefined,
                            "removed",
                            oLinesExist,
                            mLinesExist,
                            widgetProvider,
                            commentPositions
                        )
                    );
                } else if (diffSection.lineChange.changeType === FileDiffBlockChangeType.Edit) {
                    result.push(
                        ...processCharDiffChanges(
                            fileName,
                            FileDiffBlockChangeType.Delete,
                            currentLinesRendered,
                            currentDisplayLine,
                            diffSection,
                            lineChanges.oLines,
                            lineChanges.oLine,
                            undefined,
                            "removed",
                            oLinesExist,
                            mLinesExist,
                            widgetProvider,
                            commentPositions
                        )
                    );

                    currentLinesRendered += lineChanges.oLines.length;

                    result.push(
                        ...processCharDiffChanges(
                            fileName,
                            FileDiffBlockChangeType.Add,
                            currentLinesRendered,
                            currentDisplayLine,
                            diffSection,
                            lineChanges.mLines,
                            undefined,
                            lineChanges.mLine,
                            "added",
                            oLinesExist,
                            mLinesExist,
                            widgetProvider,
                            commentPositions
                        )
                    );

                    editLinesRendered += Math.min(lineChanges.mLines.length, lineChanges.oLines.length);
                }

                const ellipsisLineAdded = i > 0 && lineChanges.truncatedAfter;
                if (ellipsisLineAdded) {
                    linesRendered++;
                    ellipsisLinesRendered++;
                }

                diffBlocks.push(
                    <React.Fragment key={"diff-block-container" + i}>
                        <React.Fragment key={"diff-block-section" + i}>{...result}</React.Fragment>
                        {ellipsisLineAdded && <EllipsisLine mLinesExist={mLinesExist} oLinesExist={oLinesExist} widgetProvider={widgetProvider} />}
                    </React.Fragment>
                );
            } else {
                break;
            }
        }
    }

    return (
        <>
            {contentTruncated ? (
                <MessageCard className={"margin-bottom-8"} severity={MessageCardSeverity.Warning} messageBarClassName="repos-summary-message">
                    {Resources.PartialDiffTruncated}
                </MessageCard>
            ) : null}
            <FocusZone direction={FocusZoneDirection.Vertical} focusGroupProps={{ defaultElementId: "m-diff-widget-0" }}>
                <div className="repos-summary-diff-container body-s repos-summary-diff-blocks" role="table">
                    {...diffBlocks}
                </div>
            </FocusZone>
        </>
    );
};
